import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, Tooltip } from '@mui/material'
import { CloudUpload, Edit, FlagCircle } from '@mui/icons-material'
import { cloneDeep } from 'lodash'

import { useGET_academicPeriodsSelectQuery } from 'redux/api/academicPeriodsApi'
import {
  useDELETE_assignmentMutation,
  useDELETE_assignment_attachmentsMutation,
  useGET_assignmentByIdQuery,
  usePATCH_assignmentMutation,
  usePOST_assignmentMutation,
  usePOST_assignment_attachmentsMutation,
  usePUT_assignmentMutation,
} from 'redux/api/assignmentsApi'
import {
  useGET_courseComponentsSelectQuery,
} from 'redux/api/coursesApi'
import { useGET_studentGroupsQuery } from 'redux/api/groupsApi'
import { useGET_rubricSelectQuery } from 'redux/api/rubricApi'
import { useGET_teacherProfileSelectQuery } from 'redux/api/teachersApi'
import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'
import { useGET_groupCourseSelectQuery } from 'redux/api/groupCoursesApi'
import { useGET_teamsSelectQuery } from 'redux/api/teamsApi'

import { Form, Formik } from 'formik'

import SectionTitle from 'components/atoms/SectionTitle'
import AttachmentButton from 'components/atoms/buttons/AttachmentButton'
import SubmitButton from 'components/atoms/buttons/SubmitButton'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import ColorPickerField from 'components/atoms/fields/ColorPickerField'
import InputField from 'components/atoms/fields/InputField'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'
import GradeItemList from 'components/molecules/GradeItemList'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema, validationSchemaPublished } from 'utils/formModels/assignmentModel'
import {
  assessmentTypeSelectData,
  attemptsSelectData,
  crudColorMap,
  crudIconMap,
  genderSelectData,
  scoreTypeSelectData,
  recipientsTypeSelectData,
  submissionTypeSelectData,
  weightSelectData,
  expectedTimeTypeSelectData,
} from 'utils/helpers'

import { ActionType, IAssignment, RoleType } from 'types'
import DateTimePickerField from 'components/atoms/fields/DateTimePickerField'
import { ScrollToError } from 'components/atoms/ScrollToError'
import Rubric from 'components/molecules/Rubric'
import DialogPopup from 'components/molecules/DialogPopup'
import _ from 'lodash'
import AccommodationList from 'components/utilized/common/AccommodationList'
import ModificationList from 'components/utilized/common/ModificationList'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'redux/store'
import GoalbankPage from 'pages/protected/GoalbankPage'
import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'
import { useMedia } from 'utils/customHooks/useMedia'

interface IProps {
  action: ActionType
  assignmentId: string
  viewOnly: boolean
  userRole: RoleType
  tabText?: string
  tabKey?: string
}

const AssignmentForm: FC<IProps> = ({ action, assignmentId, viewOnly, userRole, tabText }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { isUnder600px } = useMedia()

  const backToSlug = '/assignments'
  const editSlug = `/assignments/edit/${assignmentId}`

  const [rubricOpen, setRubricOpen] = useState(false)
  const [formSubmitCount, setFormSubmitCount] = useState(0)

  const [statusType, setStatusType] = useState(null)
  const [publish, setPublish] = useState(false)
  const [selectedGroupCourse, setSelectedGroupCourse] = useState(null)
  const [selectedCourseComponent, setSelectedCourseComponent] = useState(null)


  const { addResponseData, editResponseData, isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.assignment')}`,
    entityId: assignmentId,
    getEntityQuery: useGET_assignmentByIdQuery,
    createEntityMutation: usePOST_assignmentMutation,
    editEntityMutation: usePUT_assignmentMutation,
    deleteEntityMutation: useDELETE_assignmentMutation,
  })

  const {
    status,
    group,
    group_course,
    course_chapter,
    session,
    author,
    are_submissions_allowed,
    are_comments_allowed,
    is_accommodation_allowed,
    is_modification_allowed,
    accommodations,
    modifications,
    course_component,
    submission_type,
    assessment_type,
    academic_period,
    effective_datetime,
    due_datetime,
    resubmission_datetime,
    effective_datetime_email_notifications,
    due_datetime_email_notifications,
    color_hex_code,
    title,
    description,
    grading_components,
    score_type,
    rubric,
    points_maximum,
    importance,
    number_of_attempts,
    expected_time,
    expected_time_type,
    has_extra_credit,
    has_entry_points,
    extra_credit,
    entry_points,
    counts_to_final_grade,
    assessment_probe,
    attachments,
    recipient_type,
    group_course_team,
    students,
    has_peer_assessment,
    is_peer_assessment_anonymous,
    peer_assessment_due_datetime,
    peer_assessment_submissions_count,
    peer_assessment_instructions
  } = formModel()

  const serverValues = entity?.id
    ? {
        ...entity,
        due_datetime_email_notifications: entity.due_datetime_email_notifications === 'disabled' ? false : true,
        effective_datetime_email_notifications: entity.effective_datetime_email_notifications === 'disabled' ? false : true,
        role_id: entity.role,
        gender: genderSelectData().data.list.find((r) => r.id === entity?.gender),
        recipient_type: recipientsTypeSelectData().data.list.find((r) => r.id === entity?.recipient_type),
        expected_time_type: expectedTimeTypeSelectData().data.list.find((r) => r.id === entity?.expected_time_type),
        accommodations:  _.uniqBy(
          entity.accommodations.map(a => {return {category_id: a.category_id, category_title: a.category_title}}) as any[],
          a => [a.category_id, a.category_title].join()
        ).map(a => { return {
          category: {
            id: a.category_id,
            title: a.category_title
          },
          items: entity.accommodations.filter(item => a.category_id === item.category_id)
        }}),
    
        modifications:  _.uniqBy(
          entity.modifications.map(m=> {return {category_id: m.category_id, category_title: m.category_title}}) as any[],
          m => [m.category_id, m.category_title].join()
        ).map(m => { return {
          category: {
            id: m.category_id,
            title: m.category_title
          },
          items: entity.modifications.filter(item => m.category_id === item.category_id)
        }}),

      }
    : {}

    const formValues = {
      ...initialValues,
      ...serverValues,
    }
  
  

  const {data: teachers} = useGET_teacherProfileSelectQuery({group_course: selectedGroupCourse}, {skip: !selectedGroupCourse})
  

  const [PATCH_assignment, { isLoading: assignmentEditLoading, data: patchResponseData }] =
    usePATCH_assignmentMutation()
  const [POST_assignment_attachments, { isLoading: assignmentSaveLoading }] =
    usePOST_assignment_attachmentsMutation()
  const [DELETE_assignment_attachments, { isLoading: assignmentDeleteLoading }] =
    useDELETE_assignment_attachmentsMutation()

  useEffect(() => {
    if (patchResponseData?.status) {
      navigate(backToSlug)
    }
  }, [patchResponseData?.status, navigate])

  

  useEffect(() => {
    setStatusType(formValues?.status?.id ?? null);
    setSelectedGroupCourse(formValues?.group_course?.id ?? null);

  }, [formValues,statusType,selectedGroupCourse])
 

  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))
  }, [entity])
  


  const disabledDraft = viewOnly || statusType !== 'draft'
  const disabledPublished = viewOnly || (statusType !== 'draft' && statusType !== 'published' )


  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={() => {
        if(statusType === 'published')
         return validationSchemaPublished(userRole)
        else
          return validationSchema(userRole)
      }}
      onSubmit={(values: IAssignment) => {


        const gradingComponents = values.grading_components.map((component) => ({
          ...component,
          importance: component?.importance?.id,
        }))


        if (statusType === 'published' && action === 'edit') {
        
          const data = {
            //status: values?.status?.id,
            due_datetime: values?.due_datetime,
            resubmission_datetime: values?.resubmission_datetime,
          }
          PATCH_assignment({ id: assignmentId, data })

        } else {

        
          const clonedValues = cloneDeep(values)
          if (values?.score_type?.id !== 'points') {
            delete clonedValues.points_maximum
          }

          if(userRole !== 'teacher'){
            clonedValues.author_id =  (selectedGroupCourse && teachers.length > 0) ? teachers[0].id : null
          }
            
          
          let student_ids = []
          if(values?.recipient_type?.id === 'team' && values?.group_course_team)
            student_ids = values?.group_course_team?.students?.map(s => s.id)
          if(values?.recipient_type.id === 'individuals')
            student_ids = values?.students?.map(s => s.id)

          let data = {
            ...clonedValues,
            status: publish ? 'published' : statusType,
            author_id: values?.author?.id,
            group_course_id: values?.group_course?.id,
            /* course_chapter_id: values?.course_chapter?.id, */
            submission_type: values?.submission_type?.id,
            assessment_type: values?.assessment_type?.id,
            academic_period_id: values?.academic_period?.id,
            number_of_attempts: values?.number_of_attempts?.id,
            course_component_id: values?.course_component?.id,
            rubric_id: values?.rubric?.id,
            session_id: values?.session?.id,
            importance: values?.importance?.id,
            score_type: values?.score_type?.id,
            expected_time_type: values?.expected_time_type?.id,
            entry_points: parseInt((values?.entry_points ?? 0).toString()).toString(),
            extra_credit: parseInt((values?.extra_credit ?? 0).toString()).toString(),
            recipient_type: values?.recipient_type?.id,
            group_course_team_id: values?.group_course_team?.id,
            students_ids: student_ids,
            grading_components: gradingComponents,
            effective_datetime_email_notifications: values?.effective_datetime_email_notifications ? 'send_to_all' : 'disabled',
            due_datetime_email_notifications: values?.due_datetime_email_notifications ? 'send_to_all' : 'disabled',
            accommodations_ids: values?.accommodations.map(a => a.items.map(item => item.id)).flat(),
            modifications_ids: values?.modifications.map(m => m.items.map(item => item.id)).flat(),
            goalbank_selection: goalbankSelection,
            curriculum_selection: curriculumSelection
          }

          if(!values.has_peer_assessment){
            delete data.peer_assessment_due_datetime
            delete data.peer_assessment_submissions_count
            delete data.peer_assessment_instructions
          }

          //console.log(data);
          //return;

          handleSubmit(action, assignmentId, data)
          setFormSubmitCount(formSubmitCount+1);
        }
      }}
    >
      {({ values, setFieldValue, errors }) => (
        <Form className="assignment-form">
            <ScrollToError/>
            


            {/*
              * Recepients
              */}
            <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
              <Grid item xs={12}>
                <SectionTitle text={t('assignments.assignment-to')} />
              </Grid>
              <Grid item xs={12} md={6} xl={3}>
                <SelectField
                  fieldProps={{ ...group }}
                  apiCall={useGET_studentGroupsQuery}
                  disabled={disabledDraft || action !== 'add'}
                  onChange={() => {
                    // Clear the selected value of a dependent field
                    setFieldValue('group_course', null)
                    setFieldValue('course_chapter', null)
                    setFieldValue('session', null)
                    setFieldValue('author', null)
                    setFieldValue('group_course_team', null)
                    setFieldValue('students', [])
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} xl={3}>
                <Tooltip
                  title={
                    values.group
                      ? null
                      : `${t('common.dependent-field-required', { filedName: t('common.group') })}`
                  }
                  arrow
                  placement='bottom'
                >
                  <div>
                    <SelectField
                      fieldProps={{ ...group_course }}
                      apiCall={useGET_groupCourseSelectQuery}
                      filters={{ group: values?.group?.id }}
                      disabled={disabledDraft || action !== 'add' || !values.group}
                      onChange={(newValue) => {
                        // Clear the selected value of a dependent field
                        setFieldValue('course_chapter', null)
                        setFieldValue('session', null)
                        setFieldValue('author', null)
                        setFieldValue('group_course_team', null)
                        setFieldValue('students', [])

                        setSelectedGroupCourse(newValue?.id ?? null);

                      }}
                    />
                  </div>
                </Tooltip>
              </Grid>

              {values?.group_course && 
              <Grid item xs={12} md={6} xl={3}>
                <SelectField
                  fieldProps={{ ...recipient_type }}
                  apiCall={recipientsTypeSelectData}
                  disabled={disabledDraft}
                  onChange={(newValue) => {
                    setFieldValue('group_course_team', null)
                    setFieldValue('students', [])
                  }}
                />
              </Grid>
              }

              {values?.group_course && values?.recipient_type?.id === 'team' &&
              <Grid item xs={12} md={6} xl={3}>
                <SelectField
                  fieldProps={{ ...group_course_team }}
                  apiCall={useGET_teamsSelectQuery}
                  filters={{ group_course: values?.group_course?.id }}
                  disabled={disabledDraft}
                />
              </Grid>
              }

            {values?.group_course && values?.recipient_type?.id === 'individuals' &&
              <Grid item xs={12} md={6} xl={3}>
                <SelectField
                  multiple={true}
                  fieldProps={{ ...students }}
                  apiCall={useGET_studentsProfileSelectQuery}
                  filters={{ group_course: values?.group_course?.id }}
                  disabled={disabledDraft}
                />
              </Grid>
              }
            </Grid>
                

            
            <Grid container spacing={4}  sx={{ marginTop: '0rem' }}>

              <Grid item xs={12}>
                <SectionTitle text={`${t('assignments.assignment-details')}`} />
              </Grid>

              <Grid item xs={12} md={5} xl={7}>
                <InputField fieldProps={{ ...title }} disabled={disabledDraft} />
              </Grid>

              <Grid item xs={12} md={4} xl={3}>
                <SelectField
                    fieldProps={{ ...course_component }}
                    apiCall={useGET_courseComponentsSelectQuery}
                    filters={  values?.group_course?.id
                        ? { group_course: values?.group_course?.id, is_disabled: false }
                        : { default_only: true }
                    }
                    disabled={disabledDraft}
                    onChange={(newValue) => {
                      setSelectedCourseComponent(newValue?.id ?? null);
                    }}
                    onReload={(newList) => {

                      const checkCourseComponent = selectedCourseComponent || serverValues[course_component.name]?.id
                      
                      if(checkCourseComponent && newList.filter(c => c.parent?.id==checkCourseComponent || c.id==checkCourseComponent).length==1){
                        setFieldValue(course_component.name, newList.find(c => c.parent?.id==checkCourseComponent || c.id==checkCourseComponent))
                      }else{
                        setFieldValue(course_component.name, null)
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3} xl={2}>
                  <ColorPickerField fieldProps={{ ...color_hex_code }} disabled={disabledDraft} />
                </Grid>
            </Grid>




            <Grid container spacing={4} sx={{ marginTop: '0rem' }}>
              <Grid item xs={12}>
                <SectionTitle text={`${t('common.assessment')}`} />
              </Grid>


              <Grid item xs={12} md={2}>
                <SelectField
                  fieldProps={{ ...score_type }}
                  apiCall={scoreTypeSelectData}
                  disabled={disabledDraft}
                  onChange={(value: { id: string; title: string }) => {
                    if (value?.id === 'points') {
                      setFieldValue('points_maximum', 5)
                    } else {
                      setFieldValue('session', null)
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <SelectField
                  fieldProps={{ ...submission_type }}
                  apiCall={submissionTypeSelectData}
                  disabled={disabledDraft}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SelectField
                  fieldProps={{ ...assessment_type }}
                  apiCall={assessmentTypeSelectData}
                  disabled={disabledDraft}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <div style={{ display: 'flex', width: '100%' }}>
                  <div style={{ width: '100%' }}>
                      <SelectField
                      fieldProps={{ ...rubric }}
                      apiCall={useGET_rubricSelectQuery}
                      filters={{ group_course: values?.group_course?.id ?? undefined }}
                      disabled={disabledDraft}
                    />
                  </div>
                  
                  {values?.rubric && 
                      <Button
                        variant='outlined'
                        color='black'
                        className='small-button'
                        onClick={() => {
                          setRubricOpen(true)
                        }}
                        sx={{ marginLeft: '5px' }}
                      >
                        {t('common.preview')}
                      </Button>
                  }
                  
                </div>
              </Grid>


              <Grid item xs={12} md={4} xl={2}>
                <InputField
                  fieldProps={{ ...points_maximum }}
                  disabled={disabledDraft}
                />
              </Grid>
              <Grid item xs={12} md={4} xl={2}>
                <SelectField
                  fieldProps={{ ...importance }}
                  apiCall={weightSelectData}
                  disabled={disabledDraft}
                />
              </Grid>
              <Grid item xs={12} md={4} xl={2}>
                <SelectField
                  fieldProps={{ ...number_of_attempts }}
                  apiCall={attemptsSelectData}
                  disabled={disabledDraft}
                />
              </Grid>
              <Grid item xs={12} md={4} xl={3}>
                  <InputField
                      fieldProps={{ ...expected_time }}
                      disabled={disabledDraft}
                    />
              </Grid>
              <Grid item xs={12} md={4} xl={3}>
                    <SelectField
                      fieldProps={{ ...expected_time_type }}
                      apiCall={expectedTimeTypeSelectData}
                      disabled={disabledDraft}
                    />
              </Grid>



              <Grid item xs={12} md={3}>      
                <SelectField
                  fieldProps={{ ...academic_period }}
                  apiCall={useGET_academicPeriodsSelectQuery}
                  filters={{ group_courses: values?.group_course?.id }}
                  disabled={disabledDraft}
                />
              </Grid>  
              <Grid item xs={12} md={3}>
                <DateTimePickerField fieldProps={{ ...effective_datetime }} disabled={disabledDraft} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePickerField fieldProps={{ ...due_datetime }} disabled={disabledPublished} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePickerField fieldProps={{ ...resubmission_datetime }} disabled={disabledPublished} />
              </Grid>
  


              <Grid item xs={12} md={4}>
                <CheckboxField fieldProps={{ ...assessment_probe }} disabled={disabledDraft} />  
              </Grid>    
              <Grid item xs={12} md={4}>
                <div style={{ display: 'flex', alignItems: isUnder600px ? 'start' : 'center', flexDirection: isUnder600px ? 'column' : 'row'  }}>
                  <CheckboxField fieldProps={{ ...has_entry_points }} disabled={disabledDraft} />  
                  
                  {values?.has_entry_points && 
                    <InputField
                    fieldProps={{ ...entry_points }}
                    disabled={disabledDraft}
                    />
                  }
                </div>
              </Grid>      
              <Grid item xs={12} md={4}>
                <div style={{ display: 'flex', alignItems: isUnder600px ? 'start' : 'center', flexDirection: isUnder600px ? 'column' : 'row'  }}>
                  <CheckboxField fieldProps={{ ...has_extra_credit }} disabled={disabledDraft} /> 
                
                  {values?.has_extra_credit && 
                    <InputField
                    fieldProps={{ ...extra_credit }}
                    disabled={disabledDraft}
                    />
                }
                </div>
              </Grid>      
              <Grid item xs={12} md={4}>
                <CheckboxField fieldProps={{ ...counts_to_final_grade }} disabled={disabledDraft} />  
              </Grid>      


              <Grid item xs={12} md={4}>
                <CheckboxField  fieldProps={{ ...effective_datetime_email_notifications }} disabled={disabledDraft} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CheckboxField fieldProps={{ ...due_datetime_email_notifications }} disabled={disabledDraft} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CheckboxField fieldProps={{ ...are_comments_allowed }} disabled={disabledDraft} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CheckboxField fieldProps={{ ...are_submissions_allowed }} disabled={disabledDraft} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CheckboxField fieldProps={{ ...has_peer_assessment }} disabled={disabledDraft} 
                  onChange={() => {
                    // Clear the selected value of a dependent field
                    setFieldValue('peer_assessment_instructions', '')
                    setFieldValue('peer_assessment_submissions_count', '')
                    setFieldValue('peer_assessment_due_datetime', '')
                    setFieldValue('is_peer_assessment_anonymous', false)
                  }}
                />
              </Grid>
            </Grid>

    
            {values?.has_peer_assessment && 
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <SectionTitle text={`${t('common.peer-assessments')}`} />
              </Grid>
              <Grid item xs={12}>
                <RichTextField
                    fieldProps={{ ...peer_assessment_instructions }}
                    label={peer_assessment_instructions.label}
                    disabled={disabledDraft}
                  />
              </Grid>
              <Grid item xs={6} md={4}>
                <DateTimePickerField fieldProps={{ ...peer_assessment_due_datetime }} disabled={disabledPublished} />
              </Grid>
              <Grid item xs={6} md={4}>
                <InputField fieldProps={{ ...peer_assessment_submissions_count }} disabled={disabledDraft} />
              </Grid>
              <Grid item xs={6} md={4}>
                <CheckboxField fieldProps={{ ...is_peer_assessment_anonymous }} disabled={disabledDraft} />
              </Grid>
            </Grid>
            }


            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '60px', marginBottom: '20px' }}>
              <div style={{ fontSize: '20px'}}>{t('assignments.select-standards')}</div>
              <div>
                <Button
                  onClick={() => setGoalbankOpen(true)}
                  aria-label={`${t('goalbank.use-curriculum-library')}`}
                  size='small'
                  disabled={viewOnly}
                  color='success'
                  variant='contained'
                  sx={{ boxShadow: 'none',  minHeight: '47px',  }}
                  >
                    <FlagCircle style={{ fontSize: '1.5rem' }} /> {t('goalbank.use-curriculum-library')}
                </Button>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection} />
            </div>
               
              

            <Box className="transparent-background" sx={{ marginTop: '80px', marginBottom: '40px' }}>
              <SectionTitle text={`${t('common.objectives')}`} />
              <GradeItemList fieldProps={{ ...grading_components }} disabled={disabledDraft} />
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12}>
                <RichTextField
                  fieldProps={{ ...description }}
                  label={description.label}
                  disabled={disabledDraft}
                />
              </Grid>
              {!disabledDraft &&
              <Grid item xs={12}>
                <AttachmentButton
                  fieldProps={attachments}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  disabled={disabledDraft}
                  id={addResponseData?.id || editResponseData?.id || patchResponseData?.id}
                  formSubmitCount={formSubmitCount}
                  savedAttachmentsKey={'materials'}
                  saveAttachmentsApiCall={POST_assignment_attachments}
                  deleteAttachmentsApiCall={DELETE_assignment_attachments}
                />
              </Grid>
              }

              <Grid item xs={12} sx={{ display: 'flex' }}>

              </Grid>

           </Grid>






            <Grid container spacing={4}>  
 
              <Grid item xs={12}>
                <SectionTitle text={t('common.accommodations-and-modifications')} />
              </Grid>
                 
              <Grid item xs={12} md={3}>
                <CheckboxField fieldProps={{ ...is_accommodation_allowed }} disabled={disabledDraft} />
              </Grid>
          
              <Grid item xs={12} md={9}> 
                {values?.is_accommodation_allowed && 
                  <AccommodationList fieldProps={{ ...accommodations }}/>
                }
              </Grid>       

              <Grid item xs={12} md={3}>
                <CheckboxField fieldProps={{ ...is_modification_allowed }} disabled={disabledDraft} />
              </Grid>

              <Grid item xs={12} md={9}> 
                {values?.is_modification_allowed && 
                  <ModificationList fieldProps={{ ...modifications }}/>
                }
              </Grid>  
            </Grid>     
     
          { /* </Collapse> */ }





          {/* ACTIONS */}
          <Grid container spacing={4} sx={{ marginTop: '1rem' }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }} className="transparent-background">
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>
                {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('actions.edit')}`}
                  </Button>
                </Box>
              }
                {action !== 'view' && (<div style={{ display: 'flex', marginLeft: 'auto' }}>
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    onClick={() => {
                      setPublish(false)
                    }}
                    isLoading={
                      isLoading || assignmentSaveLoading || assignmentDeleteLoading || assignmentEditLoading
                    }
                  />
                  { action !== 'delete' && statusType === 'draft' &&
                    <SubmitButton
                      color={crudColorMap[action]}
                      text={`${t('common.publish')}`}
                      icon={<CloudUpload />}
                      sx={{ marginLeft: '10px' }}
                      onClick={() => {
                        setPublish(true)
                      }}
                      isLoading={
                        isLoading || assignmentSaveLoading || assignmentDeleteLoading || assignmentEditLoading
                      }
                    /> 
                  }
                  </div>
                )}
              </Box>
            </Grid>
          </Grid>

          {values?.rubric?.id &&
          <DialogPopup
            title={values?.rubric?.title}
            open={rubricOpen}
            onClose={() => setRubricOpen(false)}
            popupWidth={'1000px'}
          >
           <Rubric id={values.rubric.id} />   
          </DialogPopup>
          }


            <DialogPopup
              title={t('common.goalbank')}
              open={goalbankOpen}
              onClose={() => setGoalbankOpen(false)}
              popupWidth={'1400px'}
              >
                  <GoalbankPage disabledSections={['goalbank_lessons']} hasSelection={true} />
        </DialogPopup>

        </Form>
      )}
    </Formik>           
  )
}

export default AssignmentForm
